import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import {parseJsonAttribute} from "../shared/blockAttributes";
import PrintoutFieldNodeView from "./PrintoutFieldNodeView";
import {
    clampFieldHeightMm,
    DEFAULT_FIELD_ATTRS,
    getFieldAttrs,
} from "./printoutFieldOptions";
import type {FieldTextStyle, PrintoutFieldAttrs} from "./printoutFieldOptions";

const textAttribute = (attributeName: string, dataAttribute: string, fallback: string | null) => ({
    default: fallback,
    parseHTML: (element: HTMLElement) => element.getAttribute(dataAttribute) ?? fallback,
    renderHTML: (attributes: Record<string, unknown>) => {
        const value = attributes[attributeName];

        return typeof value === "string" && value ? {[dataAttribute]: value} : {};
    },
});

export const PrintoutFieldNode = Node.create({
    name: "printoutField",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            align: textAttribute("align", "data-printout-field-align", DEFAULT_FIELD_ATTRS.align),
            fillColor: textAttribute("fillColor", "data-printout-field-fill-color", null),
            label: textAttribute("label", "data-printout-field-label", DEFAULT_FIELD_ATTRS.label),
            lineColor: textAttribute("lineColor", "data-printout-field-line-color", DEFAULT_FIELD_ATTRS.lineColor),
            lineStyle: textAttribute("lineStyle", "data-printout-field-line-style", DEFAULT_FIELD_ATTRS.lineStyle),
            heightMm: {
                default: DEFAULT_FIELD_ATTRS.heightMm,
                parseHTML: (element: HTMLElement) => clampFieldHeightMm(element.getAttribute("data-printout-field-height-mm")),
                renderHTML: (attributes: Record<string, unknown>) => ({
                    "data-printout-field-height-mm": clampFieldHeightMm(attributes.heightMm),
                }),
            },
            labelStyle: {
                default: DEFAULT_FIELD_ATTRS.labelStyle,
                parseHTML: (element: HTMLElement) => (
                    parseJsonAttribute<FieldTextStyle>(
                        element.getAttribute("data-printout-field-label-style"),
                        "data-printout-field-label-style",
                    ) ?? DEFAULT_FIELD_ATTRS.labelStyle
                ),
                renderHTML: (attributes: Record<string, unknown>) => ({
                    "data-printout-field-label-style": JSON.stringify(attributes.labelStyle),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-field"]',
            },
        ];
    },

    renderHTML({HTMLAttributes, node}) {
        const attrs = getFieldAttrs(node.attrs as PrintoutFieldAttrs);

        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-field",
                "data-printout-field-line-style": attrs.lineStyle,
                class: "printout-rendered-field",
                style: [
                    `--printout-field-fill-color:${attrs.fillColor ?? "transparent"}`,
                    `--printout-field-height:${attrs.heightMm}mm`,
                    `--printout-field-line-color:${attrs.lineColor}`,
                    "width:100%",
                ].join(";"),
            }),
            [
                "div",
                {
                    class: "printout-rendered-field-input",
                },
            ],
            [
                "div",
                {
                    class: "printout-rendered-field-label",
                    style: [
                        `text-align:${attrs.align}`,
                        `font-weight:${attrs.labelStyle.bold ? 700 : 600}`,
                        `font-style:${attrs.labelStyle.italic ? "italic" : "normal"}`,
                    ].join(";"),
                },
                attrs.label,
            ],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutFieldNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
